import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { Dialog, DialogReason, DialogDetail } from "./Dialog";
import { CopyButton } from "./CopyButton";
import { ViewingBadge } from "./ViewingBadge";
import { ModalAction } from "./ModalIcon";
import { EvidenceImage } from "./EvidenceImage";
import "./clientReviewVersions.css";
import "./dialog.css";
import "./wrappingButtons.css";

import type {
  ClientReviewVersion,
  ClientReviewVersionHistory as HistoryData,
} from "../types/clientReview";
export type ReviewVersionItem = ClientReviewVersion;
export type ReviewVersionHistoryData = HistoryData;
export { useVersionCaptureRequest } from './useVersionCaptureRequest';
// Keep the originating control through History → New/Compare transitions.
const dialogOpeners = new WeakMap<Document, HTMLElement>();
export function versionLabel(v: ReviewVersionItem) {
  return `V${v.sequence}${v.name ? ` · ${v.name}` : ""}`;
}
export function VersionDialog({
  title,
  icon = "archive",
  close,
  children,
  footer,
  wide = false,
}: {
  title: string;
  icon?: "archive" | "capture";
  close: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const id = useId();
  useEffect(() => {
    const focused = document.activeElement as HTMLElement | null;
    const opener = focused && focused !== document.body && !focused.closest('[data-review-version-dialog]')
      ? focused : dialogOpeners.get(document);
    if (opener) dialogOpeners.set(document, opener);
    ref.current?.showModal();
    return () => {
      ref.current?.close();
      requestAnimationFrame(() => {
        if (document.querySelector('[data-review-version-dialog] dialog[open]')) return;
        if (opener?.isConnected) opener.focus();
      });
    };
  }, []);
  return (
    <div className="oq-auto-review-ui" data-review-version-dialog>
      <Dialog
        dialogRef={ref}
        title={title}
        titleId={id}
        icon={icon}
        width="wide"
        onClose={close}
        onCancel={close}
        onClick={(e) => {
          if (e.target === e.currentTarget) close();
        }}
        footer={footer}
      >
        {wide ? <div className="vv-compare-dialog">{children}</div> : children}
      </Dialog>
    </div>
  );
}
function VersionLink({
  label,
  url,
  selected,
  onOpen,
}: {
  label: string;
  url: string;
  selected: boolean;
  onOpen: () => void;
}) {
  const [failed, setFailed] = useState(false);
  return (
    <>
      <div className="vv-history-share">
        <input
          className="vv-input vv-history-link"
          readOnly
          value={url}
          title={url}
          aria-label={`${label} client link`}
          onFocus={(e) => e.currentTarget.select()}
        />
        <CopyButton
          className="oq-btn oq-btn--secondary"
          label={`Copy ${label} link`}
          copyText={() => url}
          onCopy={() => setFailed(false)}
          onCopyError={() => setFailed(true)}
        />
        {selected ? (
          <ViewingBadge className="vv-current" />
        ) : (
          <button
            type="button"
            className="oq-btn oq-btn--secondary"
            onClick={onOpen}
          >
            Open
          </button>
        )}
      </div>
      {failed && (
        <p role="alert" className="vv-error oq-text-caption">
          Could not copy. Select this link above and copy it manually.
        </p>
      )}
    </>
  );
}
export function ClientReviewVersionHistory({
  data,
  onClose,
  onOpen,
  onNew,
  onCompare,
  onEarlier,
  earlierUrl,
  onRetry,
  error,
}: {
  data: ReviewVersionHistoryData;
  onClose: () => void;
  onOpen: (v: ReviewVersionItem) => void;
  onNew: () => void;
  onCompare: () => void;
  onEarlier?: () => void;
  earlierUrl?: string;
  onRetry?: (version: ReviewVersionItem) => void;
  error?: string;
}) {
  const ready = data.versions.filter((v) => v.status === "ready");
  const currentSequence =
    ready.find((v) => v.id === data.currentVersionId)?.sequence ?? 0;
  const attempt =
    data.versions.find((v) => v.status === "capturing") ??
    data.versions.find(
      (v) => v.status === "failed" && v.sequence > currentSequence,
    );
  return (
    <VersionDialog
      title="Version history"
      close={onClose}
      footer={
        data.canCompareVersions || data.canCreateVersion ? (
          <>
            {data.canCompareVersions && (
              <button
                type="button"
                className="oq-btn oq-btn--secondary"
                disabled={ready.length < 2}
                onClick={onCompare}
              >
                Compare versions
              </button>
            )}
            {data.canCreateVersion && (
              <button
                type="button"
                className="oq-btn oq-btn--primary"
                onClick={onNew}
                disabled={data.versions.some((v) => v.status === "capturing")}
              >
                <ModalAction kind="add">New version</ModalAction>
              </button>
            )}
          </>
        ) : undefined
      }
    >
      <div className="vv-history">
        {error && <p role="alert" className="vv-error oq-text-body">{error}</p>}
        {attempt && (
          <div
            className={`vv-progress ${attempt.status === "failed" ? "vv-progress--error" : ""}`}
            role={attempt.status === "failed" ? "alert" : "status"}
          >
            <p className="oq-text-body">
              {attempt.status === "capturing"
                ? "Capturing the new version. Your current saved page remains available."
                : "The new version could not be captured. Your current saved page remains available."}
            </p>
            {attempt.status === "failed" &&
              data.canCreateVersion &&
              onRetry && (
                <button
                  type="button"
                  className="oq-btn oq-btn--secondary"
                  onClick={() => onRetry(attempt)}
                >
                  Try again
                </button>
              )}
          </div>
        )}
        {[
          {
            title: "Current",
            versions: ready.filter((v) => v.id === data.currentVersionId),
          },
          {
            title: "Older versions",
            versions: ready.filter((v) => v.id !== data.currentVersionId),
          },
        ]
          .filter((g) => g.versions.length)
          .map((g) => (
            <section key={g.title} className="vv-history-section">
              <h3 className="oq-text-heading vv-muted">{g.title}</h3>
              <div>
                {g.versions.map((v) => (
                  <div
                    className="vv-history-row"
                    key={v.id}
                    aria-current={
                      v.id === data.selectedVersionId ? "true" : undefined
                    }
                  >
                    <div className="vv-history-summary">
                      <div className="vv-history-top">
                        <strong className="oq-text-heading">
                          {versionLabel(v)}
                        </strong>
                        <p className="vv-history-date oq-text-caption vv-muted">
                          {v.capturedAt
                            ? new Date(v.capturedAt).toLocaleDateString(
                                undefined,
                                {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                },
                              )
                            : "Capture date unknown"}
                        </p>
                      </div>
                      <p className="oq-text-body">
                        {v.commentCount}{" "}
                        {v.commentCount === 1 ? "comment" : "comments"} ·{" "}
                        {v.doneReviewers.length
                          ? `${v.doneReviewers.join(", ")} finished reviewing`
                          : "Review in progress"}
                      </p>
                    </div>
                    {v.artifactWarning && (
                      <p className="oq-text-caption vv-muted">
                        {v.artifactWarning}
                      </p>
                    )}
                    <VersionLink
                      label={versionLabel(v)}
                      url={v.shareUrl}
                      selected={v.id === data.selectedVersionId}
                      onOpen={() => onOpen(v)}
                    />
                  </div>
                ))}
              </div>
            </section>
          ))}
        {!!data.earlierCommentCount && onEarlier && (
          <section className="vv-history-section">
            <h3 className="oq-text-heading vv-muted">Earlier feedback</h3>
            <p className="oq-text-body">
              {data.earlierCommentCount} {data.earlierCommentCount === 1 ? 'comment' : 'comments'} saved before page versions
              were available.
            </p>
            {earlierUrl && (
              <VersionLink
                label="Earlier feedback"
                url={earlierUrl}
                selected={!!data.isEarlierFeedback}
                onOpen={onEarlier}
              />
            )}
          </section>
        )}
        {!ready.length && (
          <p className="oq-text-body">
            No saved versions yet. Capture a new version to preserve this page
            and its feedback.
          </p>
        )}
      </div>
    </VersionDialog>
  );
}
export function ClientReviewNewVersion({
  pageUrl,
  viewport,
  busy,
  error,
  onClose,
  onCapture,
}: {
  pageUrl: string;
  viewport: string;
  busy: boolean;
  error: string | Error;
  onClose: () => void;
  onCapture: (name: string) => void;
}) {
  const [name, setName] = useState("");
  const [invalid, setInvalid] = useState(false);
  const errorMessage = typeof error === "string" ? error : error.message;
  const captureError = (typeof error !== "string" && error.name === "NetworkError") || /^(Failed to fetch|Network request failed|Load failed|NetworkError when attempting to fetch resource\.?)$/i.test(errorMessage)
    ? "Could not confirm this capture. Try again to check its status."
    : errorMessage;
  const id = useId();
  function submit() {
    if (!name.trim()) {
      setInvalid(true);
      return;
    }
    onCapture(name.trim());
  }
  return (
    <VersionDialog
      title="New version"
      icon="capture"
      close={onClose}
      footer={
        <>
          <button
            type="button"
            className="oq-btn oq-btn--secondary"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="oq-btn oq-btn--primary"
            onClick={submit}
            disabled={busy}
          >
            <ModalAction kind="capture">
              {busy ? "Capturing…" : "Capture new version"}
            </ModalAction>
          </button>
        </>
      }
    >
      <DialogReason>Save the updated page on this client link.</DialogReason>
      <DialogDetail>
        The previous page and its feedback stay in version history. Clients will
        see the new version when they next open the link.
      </DialogDetail>
      <div className="vv-field">
        <label htmlFor={id} className="oq-text-label">
          Version name
        </label>
        <input
          id={id}
          className="vv-input"
          placeholder="For example, Homepage revisions"
          value={name}
          maxLength={100}
          autoFocus
          disabled={busy}
          onChange={(e) => {
            setName(e.target.value);
            setInvalid(false);
          }}
          aria-invalid={invalid}
          aria-describedby={invalid || error ? `${id}-error` : undefined}
        />
        {(invalid || error) && (
          <p
            id={`${id}-error`}
            className="vv-error oq-text-caption"
            role="alert"
          >
            {invalid ? "Give this version a name." : captureError}
          </p>
        )}
      </div>
      <div className="vv-summary">
        <span className="oq-text-label">Page to capture</span>
        <p className="oq-text-body">{pageUrl}</p>
        <p className="oq-text-caption vv-muted">{viewport}</p>
      </div>
      <p className="oq-text-caption vv-muted">
        Existing comments stay on their original version. Nobody is marked done
        on the new version.
      </p>
    </VersionDialog>
  );
}
export function ClientReviewVersionCompare({
  embedded = false,
  versions,
  selectedVersionId,
  onClose,
  onOpen,
  renderPicker,
  loadComments,
}: {
  /** The surrounding dialog already names the comparison. */
  embedded?: boolean;
  versions: ReviewVersionItem[];
  selectedVersionId: string | null;
  loadComments?: (version: ReviewVersionItem) => Promise<string[]>;
  onClose: () => void;
  onOpen: (v: ReviewVersionItem) => void;
  renderPicker: (
    label: string,
    value: string,
    options: { value: string; label: string }[],
    onChange: (value: string) => void,
  ) => ReactNode;
}) {
  const ready = versions.filter((v) => v.status === "ready");
  const [left, setLeft] = useState(ready[1]?.id ?? "");
  const [right, setRight] = useState(
    selectedVersionId !== ready[1]?.id
      ? (selectedVersionId ?? ready[0]?.id)
      : ready[0]?.id,
  );
  const [view, setView] = useState("side");
  const [opacity, setOpacity] = useState(50);
  const a = ready.find((v) => v.id === left),
    b = ready.find((v) => v.id === right);
  const first = useRef<HTMLDivElement>(null),
    second = useRef<HTMLDivElement>(null);
  const [conversation, setConversation] = useState<
    Record<string, { comments: string[]; error: boolean }>
  >({});
  useEffect(() => {
    let current = true;
    if (loadComments)
      for (const version of [a, b])
        if (version) {
          setConversation((value) => ({
            ...value,
            [version.id]: { comments: [], error: false },
          }));
          void loadComments(version)
            .then((comments) => {
              if (current)
                setConversation((value) => ({
                  ...value,
                  [version.id]: { comments, error: false },
                }));
            })
            .catch(() => {
              if (current)
                setConversation((value) => ({
                  ...value,
                  [version.id]: { comments: [], error: true },
                }));
            });
        }
    return () => {
      current = false;
    };
  }, [left, right]);
  const sameViewport = Boolean(
    a?.viewportWidth && a?.viewportHeight &&
    a.viewportWidth === b?.viewportWidth &&
    a.viewportHeight === b?.viewportHeight,
  );
  useEffect(() => {
    if (!sameViewport || !a?.screenshotUrl || !b?.screenshotUrl) setView("side");
  }, [sameViewport, a?.screenshotUrl, b?.screenshotUrl]);
  if (!a || !b) return null;
  const dimensions = (version: ReviewVersionItem) => version.viewportWidth && version.viewportHeight
    ? `${version.viewportWidth} × ${version.viewportHeight}` : "Viewport unknown";
  const options = ready.map((v) => ({ value: v.id, label: versionLabel(v) }));
  function sync(from: HTMLDivElement | null, to: HTMLDivElement | null) {
    if (from && to && Math.abs(from.scrollTop - to.scrollTop) > 1)
      to.scrollTop = from.scrollTop;
  }
  return (
    <section
      className="vv-comparison oq-auto-review-ui"
      aria-label="Compare saved versions"
    >
      <div className="vv-compare-bar">
        <div>
          {!embedded && <h2 className="oq-text-title">Compare versions</h2>}
          <p className="oq-text-caption vv-muted">
            Saved captures · {sameViewport ? dimensions(a) : `A: ${dimensions(a)} · B: ${dimensions(b)}`} · Comments stay with their version
          </p>
        </div>
        <div className="vv-row">
          <button
            type="button"
            className="oq-btn oq-btn--secondary"
            aria-pressed={view === "side"}
            onClick={() => setView("side")}
          >
            Side by side
          </button>
          <button
            type="button"
            className="oq-btn oq-btn--secondary"
            aria-pressed={view === "overlay"}
            disabled={!sameViewport || !a.screenshotUrl || !b.screenshotUrl}
            onClick={() => setView("overlay")}
          >
            Overlay
          </button>
          <button
            type="button"
            className="oq-btn oq-btn--secondary"
            onClick={onClose}
          >
            <ModalAction kind="cancel">Close comparison</ModalAction>
          </button>
        </div>
      </div>
      <div className="vv-pair">
        {renderPicker(
          "Version A",
          left,
          options.filter((o) => o.value !== right),
          setLeft,
        )}
        {renderPicker(
          "Version B",
          right,
          options.filter((o) => o.value !== left),
          setRight,
        )}
      </div>
      {!sameViewport && (
        <p className="vv-progress oq-text-caption">
          These captures have different or unknown viewport sizes. Layout changes
          may come from the viewport; overlay is unavailable.
        </p>
      )}
      {view === "overlay" && (
        <div className="vv-overlay-controls">
          <label className="oq-text-label">
            Version B opacity{" "}
            <input
              type="range"
              min="0"
              max="100"
              value={opacity}
              onChange={(e) => setOpacity(Number(e.target.value))}
            />
          </label>
          <output className="oq-text-caption">{opacity}%</output>
        </div>
      )}
      {[a, b]
        .filter((v) => v.artifactWarning)
        .map((v) => (
          <p key={v.id} className="vv-progress oq-text-caption">
            {versionLabel(v)} · {v.artifactWarning}
          </p>
        ))}
      <div className={`vv-compare-images vv-compare-images--${view}`}>
        <div
          className="vv-image-scroll"
          ref={first}
          onScroll={() => sync(first.current, second.current)}
        >
          {a.screenshotUrl ? (
            <EvidenceImage src={a.screenshotUrl} alt={versionLabel(a)} />
          ) : (
            <p className="oq-text-body">
              This earlier capture has no comparison image. Open its saved page
              to review the original evidence.
            </p>
          )}
          {view === "overlay" && b.screenshotUrl && (
            <div
              className="vv-overlay-image"
              style={{ opacity: opacity / 100 }}
            >
              <EvidenceImage src={b.screenshotUrl} alt={versionLabel(b)} />
            </div>
          )}
        </div>
        {view === "side" && (
          <div
            className="vv-image-scroll"
            ref={second}
            onScroll={() => sync(second.current, first.current)}
          >
            {b.screenshotUrl ? (
              <EvidenceImage src={b.screenshotUrl} alt={versionLabel(b)} />
            ) : (
              <p className="oq-text-body">
                This capture has no comparison image. Open its saved page to
                review the original evidence.
              </p>
            )}
          </div>
        )}
      </div>
      <div className="vv-compare-discussion">
        {[a, b].map((v) => (
          <div key={v.id}>
            <div className="vv-row vv-spread">
              <strong className="oq-text-heading">
                {versionLabel(v)} · {v.commentCount} {v.commentCount === 1 ? "comment" : "comments"}
              </strong>
              <button
                type="button"
                className="oq-btn oq-btn--secondary oq-btn--wrap"
                onClick={() => onOpen(v)}
              >
                Open client conversation
              </button>
            </div>
            {loadComments && (
              <p className="oq-text-body">
                {conversation[v.id]?.error
                  ? "Could not load this conversation. Open the client conversation to try again."
                  : (conversation[v.id]?.comments[0] ??
                    (v.commentCount
                      ? "Loading feedback…"
                      : "No feedback on this version yet."))}
              </p>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
